import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, gbp, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  ArrowLeft, TrendingUp, Receipt, Wallet, Store, ShoppingBag, Percent,
  Loader2, AlertTriangle, FileDown, FileSpreadsheet, Search, Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const META = {
  gross: { title: "Gross Sales", icon: TrendingUp, note: "Gross = Net + VAT", txns: true },
  vat: { title: "VAT Collected", icon: Percent, note: "Output VAT collected on net sales", txns: true },
  net: { title: "Net Sales (excl VAT)", icon: Wallet, note: "Net = Gross − VAT", txns: true },
  avg: { title: "Avg Transaction", icon: ShoppingBag, note: "Avg = Gross ÷ transactions", txns: true },
  fees: { title: "Stripe Fees", icon: Receipt, note: "Stripe charges a flat fee + a percentage per successful transaction.", txns: false },
  contribution: { title: "Contribution", icon: TrendingUp, note: "Contribution = Net − COGS − Stripe fees", txns: true },
  kiosks: { title: "Active Kiosks", icon: Store, note: "Activated (live) kiosks — includes kiosks offline due to connectivity; excludes kiosks in maintenance.", txns: false },
  failed: { title: "Failed / Cancelled", icon: AlertTriangle, note: "Payments that never completed, plus refunds issued.", txns: true },
};

function StatCard({ label, value, accent }) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-5">
      <p className="text-sm text-slate-500">{label}</p>
      <p className={`mt-2 text-2xl font-bold font-admin-head ${accent || "text-slate-900 dark:text-white"}`}>{value}</p>
    </div>
  );
}

function bigValue(key, m) {
  switch (key) {
    case "gross": return gbp(m.gross_sales);
    case "vat": return gbp(m.vat);
    case "net": return gbp(m.net_sales);
    case "avg": return gbp(m.avg_transaction_value);
    case "fees": return gbp(m.processing_fees);
    case "contribution": return gbp(m.contribution_after_fees);
    case "kiosks": return `${m.active_kiosks}/${m.total_kiosks}`;
    case "failed": return m.failed_transactions;
    default: return "";
  }
}

function cards(key, m) {
  switch (key) {
    case "gross": return [["Net sales", gbp(m.net_sales)], ["VAT", gbp(m.vat)], ["Gross", gbp(m.gross_sales)], ["Transactions", m.transactions]];
    case "vat": return [["Output VAT collected", gbp(m.vat)], ["On net sales", gbp(m.net_sales)], ["Gross", gbp(m.gross_sales)], ["Transactions", m.transactions]];
    case "net": return [["Gross", gbp(m.gross_sales)], ["VAT", gbp(m.vat)], ["Net", gbp(m.net_sales)], ["Gross profit", gbp(m.gross_profit)]];
    case "avg": return [["Gross", gbp(m.gross_sales)], ["Transactions", m.transactions], ["Avg transaction", gbp(m.avg_transaction_value)], ["Units sold", m.units_sold]];
    case "fees": return [["Gross processed", gbp(m.gross_sales)], ["Stripe fees", gbp(m.processing_fees)], ["Net after fees", gbp(m.revenue_after_fees)], ["Transactions", m.transactions]];
    case "contribution": return [["Net sales", gbp(m.net_sales)], ["COGS", gbp(m.cogs)], ["Stripe fees", gbp(m.processing_fees)], ["Contribution", gbp(m.contribution_after_fees)]];
    case "kiosks": return [["Live (activated)", m.active_kiosks], ["Online now", m.online_kiosks ?? 0], ["Total kiosks", m.total_kiosks]];
    case "failed": return [["Failed / cancelled", m.failed_transactions], ["Refunds", m.refunds], ["Refunded amount", gbp(m.refunds_amount || 0)], ["Net after refunds", gbp(m.net_after_refunds ?? m.revenue_after_fees)]];
    default: return [];
  }
}

export default function MetricDetail() {
  const { metric } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const meta = META[metric] || META.gross;
  const Icon = meta.icon;

  const [data, setData] = useState(null);
  const [fees, setFees] = useState(null);
  const [txns, setTxns] = useState([]);
  const [q, setQ] = useState("");
  const [clearing, setClearing] = useState(false);

  const loadAll = () => {
    api.get("/reports/dashboard").then((r) => setData(r.data)).catch(() => {});
    if (meta.txns) api.get("/payments/transactions").then((r) => setTxns(r.data)).catch(() => {});
    if (metric === "fees") api.get("/reports/fees").then((r) => setFees(r.data)).catch(() => {});
  };
  useEffect(() => { loadAll(); }, [metric]);

  const clearFailed = async () => {
    if (!window.confirm("Delete all failed / cancelled / expired transactions? This removes abandoned checkout test data and cannot be undone.")) return;
    setClearing(true);
    try {
      const r = await api.post("/maintenance/clear-failed-transactions");
      toast.success(`Cleared ${r.data.deleted} failed / cancelled transactions`);
      loadAll();
    } catch { toast.error("Could not clear transactions"); }
    finally { setClearing(false); }
  };

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return txns;
    return txns.filter((t) =>
      (t.transaction_id || "").toLowerCase().includes(ql) ||
      (t.created_at || "").toLowerCase().includes(ql) ||
      (t.items || []).some((i) => (i.name || "").toLowerCase().includes(ql)));
  }, [txns, q]);

  const doExport = async (fmt) => {
    try {
      await downloadFile(`/reports/export?fmt=${fmt}`, `sales_report.${fmt}`);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch { toast.error("Export failed"); }
  };

  if (!data) return <div className="h-full flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  const m = data.metrics;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid={`metric-detail-${metric}`}>
      <button type="button" data-testid="metric-back" onClick={() => navigate("/admin")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 dark:hover:text-white">
        <ArrowLeft className="h-4 w-4" /> Back to dashboard
      </button>

      <div className="flex flex-wrap items-center gap-4 justify-between">
        <div className="flex items-center gap-4">
          <div className="h-14 w-14 rounded-2xl grid place-items-center text-white" style={{ background: "var(--brand-primary, #4f46e5)" }}><Icon className="h-6 w-6" /></div>
          <div>
            <h1 className="font-admin-head text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">{meta.title}</h1>
            <p className="text-3xl font-bold font-admin-head text-slate-900 dark:text-white mt-0.5" data-testid="metric-big-value">{bigValue(metric, m)}</p>
          </div>
        </div>
        {meta.txns && (
          <div className="flex gap-2">
            {metric === "failed" && user?.role === "super_duper_admin" && (
              <Button data-testid="clear-failed-btn" variant="outline" onClick={clearFailed} disabled={clearing} className="text-rose-600 border-rose-200 hover:bg-rose-50">
                {clearing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1.5" />} Clear test data
              </Button>
            )}
            <Button data-testid="metric-export-csv" variant="outline" onClick={() => doExport("csv")}><FileDown className="h-4 w-4 mr-1.5" /> CSV</Button>
            <Button data-testid="metric-export-xlsx" variant="outline" onClick={() => doExport("xlsx")}><FileSpreadsheet className="h-4 w-4 mr-1.5" /> XLSX</Button>
          </div>
        )}
      </div>

      <p className="text-sm text-slate-500">{meta.note}</p>

      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        {cards(metric, m).map(([label, value]) => <StatCard key={label} label={label} value={value} />)}
      </div>

      {metric === "fees" && (
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-5">
          <h3 className="font-semibold text-slate-900 dark:text-white mb-1">Per-transaction fee breakdown</h3>
          <p className="text-xs text-slate-400 mb-3">Stripe charges <b>{fees ? (fees.fee_percent * 100).toFixed(1) : "1.5"}%</b> + <b>{gbp(fees ? fees.fee_fixed : 0.2)}</b> per successful transaction.</p>
          {fees ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="fees-table">
                <thead><tr className="text-left text-slate-400 text-xs border-b border-slate-100 dark:border-slate-800"><th className="py-2 pr-3">Date</th><th className="pr-3 text-right">Gross</th><th className="pr-3 text-right">Flat</th><th className="pr-3 text-right">%</th><th className="pr-3 text-right">Fee</th><th className="text-right">Effective</th></tr></thead>
                <tbody>
                  {fees.rows.map((r) => (
                    <tr key={r.id} className="border-b border-slate-50 dark:border-slate-800/50">
                      <td className="py-2 pr-3 text-slate-500">{(r.created_at || "").slice(0, 10)}</td>
                      <td className="pr-3 text-right">{gbp(r.gross)}</td>
                      <td className="pr-3 text-right">{gbp(r.flat)}</td>
                      <td className="pr-3 text-right">{gbp(r.pct_fee)}</td>
                      <td className="pr-3 text-right font-medium">{gbp(r.fee)}</td>
                      <td className="text-right text-slate-400">{r.effective_pct}%</td>
                    </tr>
                  ))}
                  {!fees.rows.length && <tr><td colSpan={6} className="py-6 text-center text-slate-400">No paid transactions yet.</td></tr>}
                </tbody>
                {fees.rows.length > 0 && <tfoot><tr className="border-t-2 border-slate-200 dark:border-slate-700 font-semibold"><td className="py-2 pr-3">Total · {fees.totals.count}</td><td className="pr-3 text-right">{gbp(fees.totals.gross)}</td><td className="pr-3 text-right">{gbp(fees.totals.flat)}</td><td className="pr-3 text-right">{gbp(fees.totals.pct_fee)}</td><td className="pr-3 text-right">{gbp(fees.totals.fee)}</td><td></td></tr></tfoot>}
              </table>
            </div>
          ) : <div className="py-6 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-slate-400" /></div>}
        </div>
      )}

      {meta.txns && (
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-5">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <h3 className="font-semibold text-slate-900 dark:text-white">Transactions ({filtered.length})</h3>
            <div className="relative">
              <Search className="h-4 w-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <Input data-testid="metric-txn-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search product, id or date…" className="pl-8 h-9 w-64" />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="metric-txn-table">
              <thead>
                <tr className="text-left text-slate-400 border-b border-slate-100 dark:border-slate-800">
                  <th className="py-2 font-medium">When</th>
                  <th className="py-2 font-medium">Transaction</th>
                  <th className="py-2 font-medium">Items</th>
                  <th className="py-2 font-medium text-right">Amount</th>
                  <th className="py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => (
                  <tr key={t.session_id} data-testid={`metric-txn-row-${t.transaction_id}`} onClick={() => navigate(`/admin/financials?txn=${t.transaction_id}`)} className="border-b border-slate-50 dark:border-slate-800/50 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-900/50">
                    <td className="py-2 text-xs text-slate-500">{(t.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="py-2 text-xs font-mono-x text-slate-500">{t.transaction_id}</td>
                    <td className="py-2 text-slate-700 dark:text-slate-300">{(t.items || []).map((i) => `${i.quantity}× ${i.name}`).join(", ")}</td>
                    <td className="py-2 text-right font-semibold">{gbp(t.gross)}</td>
                    <td className="py-2">
                      {t.refunded
                        ? <span className="text-xs font-semibold text-red-500">Refunded</span>
                        : t.refund_amount > 0
                          ? <span className="text-xs font-semibold text-amber-600">Partial ({gbp(t.refund_amount)})</span>
                          : <span className="text-xs font-semibold text-emerald-600">Paid</span>}
                    </td>
                  </tr>
                ))}
                {!filtered.length && <tr><td colSpan={5} className="py-6 text-center text-slate-400">No transactions to show.</td></tr>}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-400 mt-3">Tip: click a row to open the full order (with refund) in Financials, or export the itemised report above.</p>
        </div>
      )}
    </div>
  );
}
